/* eslint-disable no-undef */
/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../store';
import * as MapActions from '../../store/maps/actions';
import get from 'lodash/get';
import { ToggleTileStateEnum } from '../materialui_wrappers/ToggleTile';
import { GOOGLE_MAP_INSTANCE_KEY } from '../common/map/GoogleMapSingleton';

// These must match in the apiUrl property in the configuration
const API_KEY_PLACEHOLDER = '{API_KEY}';
const ZOOM_PLACEHOLDER = '{ZOOM}';
const X_COORD_PLACEHOLDER = '{X_COORD}';
const Y_COORD_PLACEHOLDER = '{Y_COORD}';

interface WeatherOverlayProps {
  map: google.maps.Map;
}

class WeatherOverlay extends React.PureComponent<WeatherOverlayProps & StateProps & DispatchProps> {
  private weatherMapType?: google.maps.ImageMapType;
  private interval: any;
  private apiUrl: string;
  private apiKey: string;
  private tileWidth: number;
  private tileHeight: number;
  private refreshPeriod: number;

  public constructor(props: WeatherOverlayProps & StateProps & DispatchProps) {
    super(props);

    const { weatherOverlayConfig } = this.props;

    this.interval = undefined;

    // Get config props
    this.apiUrl = get(weatherOverlayConfig, 'apiUrl', '');
    this.apiKey = get(weatherOverlayConfig, 'apiKey', '');
    this.tileWidth = get(weatherOverlayConfig, 'tileWidth', 0);
    this.tileHeight = get(weatherOverlayConfig, 'tileHeight', 0);
    this.refreshPeriod = get(weatherOverlayConfig, 'refreshPeriodMinutes', 0);

    // Apply the API key to the API URL
    this.apiUrl = this.apiUrl.replace(API_KEY_PLACEHOLDER, this.apiKey);

    // Create the Weather Overlay
    this.weatherMapType = new google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        return this.apiUrl
          .replace(ZOOM_PLACEHOLDER, zoom.toString())
          .replace(X_COORD_PLACEHOLDER, coord.x.toString())
          .replace(Y_COORD_PLACEHOLDER, coord.y.toString());
      },
      tileSize: new google.maps.Size(this.tileWidth, this.tileHeight),
      name: 'weathermaptype',
    });
  }

  public componentDidUpdate(): void {
    if (this.shouldShowWeatherOverlay()) {
      this.refreshWeatherOverlay();
      this.interval = setInterval(this.refreshWeatherOverlay, this.refreshPeriod * 60000);
    } else {
      this.clearWeatherOverlayEnv();
    }
  }

  public componentWillUnmount(): void {
    const { setWeatherChecked } = this.props;

    this.clearWeatherOverlayEnv();
    this.weatherMapType = undefined;
    setWeatherChecked(false);
  }

  private refreshWeatherOverlay = (): void => {
    const { map } = this.props;

    map.overlayMapTypes.clear();
    if (this.weatherMapType) {
      map.overlayMapTypes.push(this.weatherMapType);
    }
  };

  private clearWeatherOverlayEnv = (): void => {
    const { map } = this.props;

    clearInterval(this.interval);
    map.overlayMapTypes.clear();
  };

  private shouldShowWeatherOverlay = () => {
    const { weatherShown } = this.props;

    return weatherShown && this.apiUrl && this.apiKey && this.tileWidth && this.tileHeight && this.refreshPeriod;
  };

  public render(): null {
    return null;
  }
}

const mapStateToProps = (state: RootState, ownProps: WeatherOverlayProps): any => ({
  weatherOverlayConfig: state.config.appConfig.weatherOverlayConfig,
  weatherShown:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.mapSettingsPrecipRadarTileState === ToggleTileStateEnum.ON,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  // TODO: when this needs to handle mutliple maps turn this component into a functional component to handle a dynamic mapId
  setWeatherChecked: MapActions.setWeatherOverlayShown(MapActions.FLEET_MAP_ID),
};
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(WeatherOverlay);
